import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import {
  ArrowRight, CheckCircle, Zap, Repeat, Package, Sparkles,
  Phone, Mail, ChevronRight, Star
} from 'lucide-react';

// ─── Hooks ──────────────────────────────────────────────────────────────────

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); observer.disconnect(); }
    }, { threshold });
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, inView];
};

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
};

// ─── Pricing Data (defaults — overridden by API fetch) ───────────────────────

const DEFAULT_FOLDPASS_PLANS = [
  { type: '10kg', kg: 10, price: 699,  months: 1, per_kg: 70, color: 'blue',   popular: false, desc: 'Great for occasional use' },
  { type: '20kg', kg: 20, price: 1199, months: 2, per_kg: 60, color: 'orange', popular: true,  desc: 'Most popular — 2 months supply' },
  { type: '40kg', kg: 40, price: 2199, months: 3, per_kg: 55, color: 'violet', popular: false, desc: 'Best value — quarterly plan' },
];

const LAUNDRY_STATIC = [
  { key: 'flexfold',  label: 'FlexFold',  price: 79,  desc: 'Washing + Ironing', delivery: '72 hrs', color: 'blue',   icon: Package, features: ['Deep wash', 'Machine dried', 'Steam ironed', 'Folded & packed'] },
  { key: 'flashfold', label: 'FlashFold', price: 99,  desc: 'Washing + Ironing', delivery: '24 hrs', color: 'orange', icon: Zap,     features: ['Express handling', 'Priority queue', 'Steam ironed', 'Same-day dispatch'] },
  { key: 'justpress', label: 'JustPress', price: 49,  desc: 'Ironing only',       delivery: '24 hrs', color: 'violet', icon: Sparkles, features: ['Steam press only', 'No washing', 'Perfect creases', 'Neatly folded'] },
];

const DRY_CLEAN_WOMEN = [
  { name: 'Kurta / Kameez', price: 99 }, { name: 'Salwaar', price: 89 }, { name: 'Saree (light)', price: 149 },
  { name: 'Saree (heavy)', price: 249 }, { name: 'Saree (cotton)', price: 149 }, { name: 'Saree (silk)', price: 199 },
  { name: 'Saree (designer)', price: 299 }, { name: 'Lehenga (normal)', price: 349 }, { name: 'Lehenga (medium)', price: 299 },
  { name: 'Lehenga (heavy)', price: 399 }, { name: 'Lehenga (designer)', price: 599 }, { name: 'Dress', price: 199 },
  { name: 'Dress (Indo-western)', price: 199 }, { name: 'Dress heavy (Indo-western)', price: 349 }, { name: 'Blouse (normal)', price: 49 },
  { name: 'Blouse (designer)', price: 99 }, { name: 'Choli, Lehenga, Dupatta', price: 599 }, { name: 'Coat (long)', price: 199 },
  { name: 'Coat (short)', price: 149 }, { name: 'Dupatta (normal)', price: 49 }, { name: 'Dupatta (heavy)', price: 99 },
  { name: 'Formal gown (normal)', price: 199 }, { name: 'Formal gown (heavy)', price: 249 }, { name: 'Gaghra 3pc (normal)', price: 499 },
  { name: 'Gaghra 3pc (designer)', price: 699 }, { name: 'Kurta + Salwar', price: 149 }, { name: 'Ladies suit 2pcs', price: 249 },
  { name: 'Ladies suit 3pcs', price: 299 }, { name: 'Night dress', price: 79 }, { name: 'Plazo pant', price: 99 },
  { name: 'Petticoat (cotton)', price: 79 }, { name: 'Petticoat (woolen)', price: 99 }, { name: 'Innerwear', price: 49 },
];

const DRY_CLEAN_MEN = [
  { name: 'Shirt / T-Shirt', price: 99 }, { name: 'Trouser / Jeans', price: 99 }, { name: 'Suit 2pcs', price: 349 },
  { name: 'Suit 3pcs', price: 449 }, { name: 'Jacket', price: 249 }, { name: 'Blazer (long)', price: 299 },
  { name: 'Blazer (short)', price: 249 }, { name: 'Shirt-Pant Combo', price: 129 }, { name: 'Jacket faux fur (long)', price: 699 },
  { name: 'Jacket faux fur (short)', price: 599 }, { name: 'Jacket puffer (long)', price: 299 }, { name: 'Jacket puffer (short)', price: 249 },
  { name: 'Jacket normal (long)', price: 299 }, { name: 'Jacket normal (short)', price: 249 }, { name: 'Kurta (heavy)', price: 149 },
  { name: 'Kurta (silk)', price: 149 }, { name: 'Kurta pajama (light)', price: 199 }, { name: 'Kurta pajama (heavy)', price: 299 },
  { name: 'Sweater (full sleeve)', price: 149 }, { name: 'Sweater (sleeveless)', price: 99 }, { name: 'Sweat shirt', price: 149 },
];

const DRY_CLEAN_KIDS = [
  { name: 'Top', price: 49 }, { name: 'Choli lehenga', price: 299 }, { name: 'Coat', price: 149 },
  { name: 'Combo (shirt-pant)', price: 79 }, { name: 'Frock (cotton)', price: 109 }, { name: 'Frock (designer)', price: 199 },
  { name: 'Normal dress', price: 109 }, { name: 'Normal dress (heavy)', price: 149 }, { name: 'Kurta 2pcs', price: 169 },
  { name: 'Frock', price: 119 },
];

const SHOE_ITEMS = [
  { name: 'Sports Shoes', price: 199, emoji: '👟', desc: 'Deep clean + deodorize' },
  { name: 'Sneaker Shoes', price: 199, emoji: '🥾', desc: 'Brush + steam clean' },
  { name: 'Leather Shoes', price: 349, emoji: '👞', desc: 'Polish + conditioning' },
  { name: 'Suede Shoes', price: 349, emoji: '🥿', desc: 'Gentle suede care' },
];

const HOME_ITEMS = [
  { name: 'Blanket (Single)', price: 299, unit: 'piece', emoji: '🛏️' },
  { name: 'Blanket (Double)', price: 399, unit: 'piece', emoji: '🛏️' },
  { name: 'Blanket Divet (Single)', price: 599, unit: 'piece', emoji: '🛏️' },
  { name: 'Blanket Divet (Double)', price: 799, unit: 'piece', emoji: '🛏️' },
  { name: 'Quilt (Single)', price: 399, unit: 'piece', emoji: '🛏️' },
  { name: 'Quilt (Double)', price: 499, unit: 'piece', emoji: '🛏️' },
  { name: 'Curtain', price: 199, unit: 'panel', emoji: '🪟' },
  { name: 'Carpet', price: 35, unit: 'sqft', emoji: '🏠' },
];

// ─── Emoji helpers ────────────────────────────────────────────────────────────
const getDcEmoji = (name) => {
  const n = name.toLowerCase();
  if (n.includes('saree') || n.includes('lehenga') || n.includes('gaghra') || n.includes('gown') || n.includes('frock')) return '👗';
  if (n.includes('kurta') || n.includes('salwaar') || n.includes('salwar') || n.includes('pajama') || n.includes('ladies suit') || n.includes('kurta + salwar')) return '👘';
  if (n.includes('shirt') || n.includes('t-shirt') || n.includes('top') || n.includes('blouse') || n.includes('choli')) return '👕';
  if (n.includes('trouser') || n.includes('jeans') || n.includes('pant') || n.includes('plazo') || n.includes('petticoat')) return '👖';
  if (n.includes('suit')) return '🤵';
  if (n.includes('blazer') || n.includes('jacket') || n.includes('coat') || n.includes('sweat')) return '🧥';
  if (n.includes('sweater')) return '🧶';
  if (n.includes('dupatta') || n.includes('scarf')) return '🧣';
  if (n.includes('innerwear') || n.includes('undergarment') || n.includes('night')) return '🩲';
  if (n.includes('dress')) return '👗';
  if (n.includes('combo')) return '👔';
  return '👔';
};

// ─── Section IDs ─────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'foldpass',  label: 'FoldPass',    icon: '🔁' },
  { id: 'laundry',   label: 'Wash & Iron', icon: '👕' },
  { id: 'dry-clean', label: 'Dry Clean',   icon: '✨' },
  { id: 'shoe-care', label: 'Shoe Care',   icon: '👟' },
  { id: 'home',      label: 'Home Linen',  icon: '🏠' },
];

// ─── PricingPage ─────────────────────────────────────────────────────────────
const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('foldpass');
  const [dcTab, setDcTab] = useState('women');
  const sectionRefs = useRef({});
  const [foldpassPlans, setFoldpassPlans] = useState(DEFAULT_FOLDPASS_PLANS);
  const [laundryServices, setLaundryServices] = useState(LAUNDRY_STATIC);
  const [laundryExtra, setLaundryExtra] = useState([]);
  const [apiDryCleanWomen, setApiDryCleanWomen] = useState(DRY_CLEAN_WOMEN);
  const [apiDryCleanMen, setApiDryCleanMen] = useState(DRY_CLEAN_MEN);
  const [apiDryCleanKids, setApiDryCleanKids] = useState(DRY_CLEAN_KIDS);
  const [apiShoeItems, setApiShoeItems] = useState(SHOE_ITEMS);
  const [apiHomeItems, setApiHomeItems] = useState(HOME_ITEMS);

  useEffect(() => {
    Promise.all([
      axios.get(`${BACKEND_URL}/api/individual/service-rates`),
      axios.get(`${BACKEND_URL}/api/individual/service-categories`).catch(() => ({ data: [] })),
    ])
      .then(([r, catRes]) => {
        const data = r.data;
        if (data.foldpass_plan?.length) {
          const PLAN_COLORS = { 10: 'blue', 20: 'orange', 40: 'violet' };
          const PLAN_DESCS  = { 10: 'Great for occasional use', 20: 'Most popular — 2 months supply', 40: 'Best value — quarterly plan' };
          setFoldpassPlans(data.foldpass_plan
            .sort((a, b) => (a.plan_kg || 0) - (b.plan_kg || 0))
            .map(r => ({
              type: `${r.plan_kg}kg`, kg: r.plan_kg, price: r.price,
              months: r.plan_months,
              per_kg: Math.round(r.price / r.plan_kg),
              color: PLAN_COLORS[r.plan_kg] || 'blue',
              popular: r.plan_popular || false,
              desc: PLAN_DESCS[r.plan_kg] || '',
            }))
          );
        }
        if (data.flexfold?.[0] || data.flashfold?.[0] || data.justpress?.[0]) {
          setLaundryServices(LAUNDRY_STATIC.map(s => {
            const r = data[s.key]?.[0];
            return r ? { ...s, price: r.price } : s;
          }));
        }
        if (data.dry_cleaning?.length) {
          const makeList = (group) => data.dry_cleaning
            .filter(r => r.item_group === group)
            .map(r => ({ name: r.catalog_item, price: r.price }));
          setApiDryCleanWomen(makeList('Women'));
          setApiDryCleanMen(makeList('Men'));
          setApiDryCleanKids(makeList('Kids'));
        }
        if (data.shoe_cleaning?.length)
          setApiShoeItems(data.shoe_cleaning.map(r => {
            const fallback = SHOE_ITEMS.find(s => s.name === r.catalog_item) || {};
            return { ...fallback, name: r.catalog_item, price: r.price };
          }));
        if (data.home_items?.length)
          setApiHomeItems(data.home_items.map(r => {
            const fallback = HOME_ITEMS.find(s => s.name === r.catalog_item) || {};
            return { ...fallback, name: r.catalog_item, price: r.price, unit: r.unit };
          }));
        if (data.laundry_extra?.length)
          setLaundryExtra(data.laundry_extra.map(r => ({ name: r.label || r.catalog_item, price: r.price, unit: r.unit })));

        // Custom admin-created kg-priced services (e.g. "Wash & Fold") — appended after the 3 built-in cards
        const custom = (catRes.data || []).filter(c => c.service_mode === 'kg' && !c.is_system && c.is_active);
        if (custom.length) {
          const CUSTOM_COLORS = ['blue', 'orange', 'violet', 'emerald'];
          const customCards = custom.map((c, i) => {
            const rate = data[c.key]?.[0];
            return {
              key: c.key,
              label: c.label,
              price: rate ? rate.price : 0,
              desc: 'Wash + Care',
              delivery: `${c.default_days * 24} hrs`,
              color: CUSTOM_COLORS[i % CUSTOM_COLORS.length],
              icon: Package,
              features: ['Professional care', 'Doorstep pickup & drop', 'Quality checked'],
            };
          });
          setLaundryServices(prev => [...prev, ...customCards]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to hash on mount
  useEffect(() => {
    const hash = location.hash?.replace('#', '');
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setActiveSection(hash);
      }, 200);
    }
  }, [location.hash]);

  // Update active section on scroll
  useEffect(() => {
    const onScroll = () => {
      for (const s of SECTIONS) {
        const el = document.getElementById(s.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 160 && rect.bottom > 160) {
            setActiveSection(s.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const colorMap = {
    blue:   { bg: 'bg-blue-600', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-700', ring: 'ring-blue-200' },
    orange: { bg: 'bg-orange-500', light: 'bg-orange-50', text: 'text-orange-500', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-700', ring: 'ring-orange-200' },
    violet: { bg: 'bg-violet-600', light: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-700', ring: 'ring-violet-200' },
    emerald: { bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', ring: 'ring-emerald-200' },
  };

  return (
    <div className="min-h-screen bg-white text-slate-800" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* ── NAV ── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-100' : 'bg-white border-b border-slate-100'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="hover:opacity-80 transition-opacity">
            <img src="/logo.png" alt="JustFold" className="h-9 w-auto object-contain" />
          </button>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-sm text-slate-500 hover:text-slate-800 transition-colors">← Back to Home</button>
            <button onClick={() => navigate('/customer/login')}
              className="text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl transition-all hover:shadow-lg hover:shadow-blue-600/25 hover:-translate-y-0.5">
              Book Now →
            </button>
          </div>
          <button onClick={() => navigate('/customer/login')}
            className="md:hidden text-sm font-semibold bg-blue-600 text-white px-4 py-2 rounded-xl">
            Book Now
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="pt-24 pb-12 px-4 bg-gradient-to-br from-blue-50 via-slate-50 to-violet-50 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[100px] opacity-40 pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-violet-100 rounded-full blur-[100px] opacity-30 pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 text-xs font-bold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
            <Star className="w-3 h-3 fill-blue-500" /> Transparent Pricing
          </div>
          <h1 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 mb-4">
            Simple, clear<br />
            <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">laundry pricing</span>
          </h1>
          <p className="text-slate-500 text-lg max-w-xl mx-auto mb-8">
            No hidden fees. No surprises. What you see is what you pay.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`text-sm font-semibold px-5 py-2.5 rounded-xl border transition-all hover:-translate-y-0.5 ${
                  activeSection === s.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-600/25'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600'
                }`}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Sticky section nav (scrolled) ── */}
      <div className={`sticky top-16 z-40 bg-white border-b border-slate-100 transition-all duration-300 ${scrolled ? 'shadow-sm' : ''}`}>
        <div className="max-w-6xl mx-auto px-4 overflow-x-auto">
          <div className="flex gap-0 min-w-max">
            {SECTIONS.map(s => (
              <button key={s.id} onClick={() => scrollTo(s.id)}
                className={`px-5 py-3.5 text-sm font-semibold border-b-2 transition-all whitespace-nowrap ${
                  activeSection === s.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
                }`}>
                {s.icon} {s.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-16 space-y-24">

        {/* ── FOLDPASS ── */}
        <section id="foldpass" className="scroll-mt-32">
          <FadeIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Repeat className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-3xl font-black text-slate-900">FoldPass</h2>
                <p className="text-sm text-slate-500">Monthly subscription — pay upfront, use over time</p>
              </div>
            </div>
            <div className="h-px bg-slate-100 mb-8" />
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {foldpassPlans.map((plan, i) => {
              const c = colorMap[plan.color];
              return (
                <FadeIn key={plan.type} delay={i * 100}>
                  <div className={`relative rounded-3xl p-7 border-2 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${
                    plan.popular
                      ? `bg-gradient-to-br from-blue-600 to-blue-700 border-blue-500 text-white shadow-2xl shadow-blue-600/25`
                      : `bg-white ${c.border} hover:${c.border}`
                  }`}>
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="bg-orange-400 text-white text-xs font-black px-4 py-1 rounded-full shadow-md">⭐ Most Popular</span>
                      </div>
                    )}
                    <div className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full mb-4 ${plan.popular ? 'bg-white/20 text-white' : c.badge}`}>
                      {plan.kg}kg Plan
                    </div>
                    <div className="mb-4">
                      <div className={`text-4xl font-black ${plan.popular ? 'text-white' : 'text-slate-900'}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                        ₹{plan.price.toLocaleString('en-IN')}
                      </div>
                      <p className={`text-sm mt-1 ${plan.popular ? 'text-blue-100' : 'text-slate-500'}`}>for {plan.kg}kg of laundry</p>
                    </div>
                    <div className={`text-3xl font-black mb-1 ${plan.popular ? 'text-orange-300' : c.text}`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                      ₹{plan.per_kg}<span className={`text-base font-normal ${plan.popular ? 'text-blue-200' : 'text-slate-400'}`}>/kg</span>
                    </div>
                    <p className={`text-xs mb-6 ${plan.popular ? 'text-blue-100' : 'text-slate-500'}`}>{plan.desc}</p>
                    <ul className="space-y-2.5 mb-6">
                      {[
                        `${plan.kg}kg total capacity`,
                        'Pick up from your doorstep',
                        'Washed, dried & folded',
                        'Use across multiple pickups',
                        plan.popular ? '✓ Best value per kg' : 'Fabric care report',
                      ].map(f => (
                        <li key={f} className="flex items-center gap-2 text-sm">
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${plan.popular ? 'bg-white/20' : c.bg}`}>
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          </div>
                          <span className={plan.popular ? 'text-blue-50' : 'text-slate-600'}>{f}</span>
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate('/individual/login')}
                      className={`w-full py-3 rounded-2xl font-bold text-sm transition-all hover:shadow-lg ${
                        plan.popular
                          ? 'bg-white text-blue-600 hover:bg-blue-50'
                          : `${c.bg} text-white hover:opacity-90`
                      }`}>
                      Get {plan.kg}kg Plan →
                    </button>
                  </div>
                </FadeIn>
              );
            })}
          </div>
          <FadeIn delay={300}>
            <div className="mt-6 bg-blue-50 border border-blue-100 rounded-2xl px-6 py-4 flex items-start gap-3">
              <div className="text-2xl">💡</div>
              <div>
                <p className="text-sm font-bold text-blue-800">How FoldPass works</p>
                <p className="text-sm text-blue-700 mt-0.5">Purchase a kg plan, then book pickups anytime. Admin weighs your clothes after pickup and deducts from your balance. No fixed schedule needed.</p>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* ── WASH & IRON (FlexFold / FlashFold / JustPress) ── */}
        <section id="laundry" className="scroll-mt-32">
          <FadeIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-3xl font-black text-slate-900">Wash & Iron</h2>
                <p className="text-sm text-slate-500">Pay per kg — FlexFold, FlashFold & JustPress</p>
              </div>
            </div>
            <div className="h-px bg-slate-100 mb-8" />
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6">
            {laundryServices.map((svc, i) => {
              const c = colorMap[svc.color];
              const Icon = svc.icon;
              return (
                <FadeIn key={svc.key} delay={i * 100}>
                  <div className={`bg-white rounded-3xl p-7 border-2 ${c.border} hover:-translate-y-2 hover:shadow-xl transition-all duration-300`}>
                    <div className={`w-12 h-12 ${c.bg} rounded-2xl flex items-center justify-center mb-5`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <h3 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-2xl font-black text-slate-900 mb-1">{svc.label}</h3>
                    <p className={`text-sm font-semibold ${c.text} mb-4`}>{svc.desc}</p>
                    <div className="mb-2">
                      <span className="text-5xl font-black text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>₹{svc.price}</span>
                      <span className="text-lg text-slate-400 ml-1">/kg</span>
                    </div>
                    <div className={`inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full ${c.badge} mb-5`}>
                      <Zap className="w-3 h-3" /> {svc.delivery} delivery
                    </div>
                    <ul className="space-y-2.5 mb-6">
                      {svc.features.map(f => (
                        <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                          <div className={`w-4 h-4 rounded-full ${c.bg} flex items-center justify-center flex-shrink-0`}>
                            <CheckCircle className="w-2.5 h-2.5 text-white" />
                          </div>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => navigate('/individual/login')}
                      className={`w-full py-3 rounded-2xl font-bold text-sm ${c.bg} text-white hover:opacity-90 hover:shadow-lg transition-all`}>
                      Book {svc.label} →
                    </button>
                  </div>
                </FadeIn>
              );
            })}
          </div>

          {/* Extra wash & iron services added by admin */}
          {laundryExtra.length > 0 && (
            <FadeIn delay={300}>
              <div className="mt-8">
                <h3 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-lg font-black text-slate-700 mb-4">
                  Additional Services
                </h3>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {laundryExtra.map((item, i) => (
                    <FadeIn key={item.name} delay={Math.min(i * 40, 200)}>
                      <div className="bg-white border border-teal-200 rounded-2xl px-4 py-3.5 flex items-center justify-between hover:border-teal-400 hover:shadow-sm transition-all group">
                        <span className="text-sm text-slate-700 font-medium group-hover:text-slate-900 truncate">{item.name}</span>
                        <span className="text-sm font-black text-teal-600 ml-3 flex-shrink-0">
                          ₹{item.price}<span className="text-xs font-normal text-slate-400">/{item.unit}</span>
                        </span>
                      </div>
                    </FadeIn>
                  ))}
                </div>
              </div>
            </FadeIn>
          )}
        </section>

        {/* ── DRY CLEAN ── */}
        <section id="dry-clean" className="scroll-mt-32">
          <FadeIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-3xl font-black text-slate-900">Dry Cleaning</h2>
                <p className="text-sm text-slate-500">Per garment pricing — professional care for delicate items</p>
              </div>
            </div>
            <div className="h-px bg-slate-100 mb-8" />
          </FadeIn>

          {/* Tab switcher */}
          <FadeIn>
            <div className="flex gap-2 mb-6 bg-slate-100 p-1 rounded-xl w-fit">
              {[
                { key: 'women', label: '👩 Women' },
                { key: 'men',   label: '👨 Men' },
                { key: 'kids',  label: '🧒 Kids' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setDcTab(tab.key)}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                    dcTab === tab.key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>
          </FadeIn>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {(dcTab === 'women' ? apiDryCleanWomen : dcTab === 'men' ? apiDryCleanMen : apiDryCleanKids).map((item, i) => (
              <FadeIn key={item.name} delay={Math.min(i * 30, 300)}>
                <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3.5 flex items-center justify-between hover:border-emerald-300 hover:shadow-sm transition-all group">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-sm text-slate-700 font-medium group-hover:text-slate-900 truncate">{item.name}</span>
                  </div>
                  <span className="text-sm font-black text-emerald-600 ml-3 flex-shrink-0">₹{item.price}</span>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={200}>
            <div className="mt-6 bg-emerald-50 border border-emerald-100 rounded-2xl px-6 py-4 flex items-start gap-3">
              <div className="text-2xl">✨</div>
              <div>
                <p className="text-sm font-bold text-emerald-800">Premium dry cleaning process</p>
                <p className="text-sm text-emerald-700 mt-0.5">Each garment is individually inspected, spot-treated, and professionally cleaned using industry-grade equipment. Stain treatment included.</p>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* ── SHOE CARE ── */}
        <section id="shoe-care" className="scroll-mt-32">
          <FadeIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center text-xl">
                👟
              </div>
              <div>
                <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-3xl font-black text-slate-900">Shoe Care</h2>
                <p className="text-sm text-slate-500">Antimicrobial + steam cleaning for all shoe types</p>
              </div>
            </div>
            <div className="h-px bg-slate-100 mb-8" />
          </FadeIn>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5">
            {apiShoeItems.map((item, i) => (
              <FadeIn key={item.name} delay={i * 80}>
                <div className="bg-white border-2 border-blue-100 rounded-3xl p-6 text-center hover:-translate-y-1 hover:shadow-lg hover:border-blue-300 transition-all duration-300">
                  <div className="text-4xl mb-3">{item.emoji}</div>
                  <h4 style={{ fontFamily: 'Manrope, sans-serif' }} className="font-black text-slate-900 mb-1">{item.name}</h4>
                  <p className="text-xs text-slate-500 mb-3">{item.desc}</p>
                  <div className="text-2xl font-black text-blue-600" style={{ fontFamily: 'Manrope, sans-serif' }}>₹{item.price}</div>
                  <p className="text-xs text-slate-400 mt-0.5">per pair</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn delay={200}>
            <div className="mt-6 bg-blue-50 border border-blue-100 rounded-2xl px-6 py-4 flex items-start gap-3">
              <div className="text-2xl">🧼</div>
              <div>
                <p className="text-sm font-bold text-blue-800">What's included in shoe care</p>
                <p className="text-sm text-blue-700 mt-0.5">Antimicrobial treatment, steam cleaning, deodorizing, and polish (for leather). Shoes are returned freshly packed.</p>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* ── HOME & LINEN ── */}
        <section id="home" className="scroll-mt-32">
          <FadeIn>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-violet-600 rounded-xl flex items-center justify-center text-xl">
                🏠
              </div>
              <div>
                <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-3xl font-black text-slate-900">Home & Linen</h2>
                <p className="text-sm text-slate-500">Blankets, quilts, curtains, and carpets</p>
              </div>
            </div>
            <div className="h-px bg-slate-100 mb-8" />
          </FadeIn>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {apiHomeItems.map((item, i) => (
              <FadeIn key={item.name} delay={i * 60}>
                <div className="bg-white border-2 border-violet-100 rounded-2xl p-5 hover:border-violet-300 hover:shadow-md transition-all">
                  <div className="text-3xl mb-2">{item.emoji}</div>
                  <h4 className="font-bold text-slate-900 text-sm mb-1">{item.name}</h4>
                  <div className="flex items-baseline gap-1">
                    <span className="text-xl font-black text-violet-600" style={{ fontFamily: 'Manrope, sans-serif' }}>₹{item.price}</span>
                    <span className="text-xs text-slate-400">/{item.unit}</span>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>

      </div>

      {/* ── CTA ── */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-600 via-blue-700 to-violet-700 mt-8">
        <FadeIn>
          <div className="max-w-3xl mx-auto text-center text-white">
            <h2 style={{ fontFamily: 'Manrope, sans-serif' }} className="text-4xl sm:text-5xl font-black mb-4">
              Ready to get started?
            </h2>
            <p className="text-blue-100 text-lg mb-8 max-w-lg mx-auto">
              Book your first pickup in under 60 seconds. No commitment required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/individual/login')}
                className="inline-flex items-center justify-center gap-2 bg-orange-400 hover:bg-orange-500 text-white font-bold text-base px-8 py-4 rounded-2xl transition-all hover:shadow-xl hover:shadow-orange-400/40 hover:-translate-y-1">
                Book Now <ArrowRight className="w-5 h-5" />
              </button>
              <a href="https://wa.me/919821381888" target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-white/15 hover:bg-white/25 border border-white/30 text-white font-semibold text-base px-8 py-4 rounded-2xl transition-all hover:-translate-y-0.5">
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* ── FOOTER (minimal) ── */}
      <footer className="bg-slate-900 text-white py-8 px-4">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/justfold-logo-transparent.png" alt="JustFold" className="h-7" style={{ filter: 'brightness(0) invert(1)' }} />
            <span className="text-xs text-slate-500">Patel Nagar, Gurgaon · contact@justfold.in</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+919821381888" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
              <Phone className="w-3 h-3" /> +91 9821381888
            </a>
            <a href="mailto:contact@justfold.in" className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white transition-colors">
              <Mail className="w-3 h-3" /> contact@justfold.in
            </a>
          </div>
        </div>
      </footer>

      <style>{`
        html { scroll-behavior: smooth; }
        .scroll-mt-32 { scroll-margin-top: 8rem; }
      `}</style>
    </div>
  );
};

export default PricingPage;
